import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { 
  Settings, Code, Copy, RefreshCw, CheckCircle, 
  Palette, Mail, DollarSign, Globe, Eye, EyeOff,
  Loader2, ExternalLink, Inbox, Clock
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TuningConfiguratorSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [embedCode, setEmbedCode] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [activeTab, setActiveTab] = useState('settings');
  
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsRes, embedRes, inquiriesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/tuning-configurator/settings`, { headers }),
        axios.get(`${BACKEND_URL}/api/tuning-configurator/embed-code`, { headers }),
        axios.get(`${BACKEND_URL}/api/tuning-configurator/inquiries?limit=20`, { headers })
      ]);
      
      setSettings(settingsRes.data);
      setEmbedCode(embedRes.data);
      setInquiries(inquiriesRes.data.inquiries || []);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/tuning-configurator/settings`,
        {
          contact_email: settings.contact_email,
          company_name: settings.company_name,
          show_prices: settings.show_prices,
          price_adjustment_percent: settings.price_adjustment_percent,
          branding: settings.branding,
          allowed_domains: settings.allowed_domains
        },
        { headers }
      );
      
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };
  
  const handleRegenerateKey = async () => {
    if (!window.confirm('Sind Sie sicher? Der alte API-Key wird ungültig.')) return;
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/tuning-configurator/regenerate-api-key`,
        {},
        { headers }
      );
      
      setSettings(prev => ({ ...prev, api_key: response.data.api_key }));
      toast.success('Neuer API-Key generiert');
      loadData(); // Reload embed code
    } catch (error) {
      toast.error('Fehler beim Generieren des Keys');
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('In Zwischenablage kopiert');
  };
  
  const updateBranding = (key, value) => {
    setSettings(prev => ({
      ...prev,
      branding: { ...prev.branding, [key]: value }
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-300 border-blue-400/30';
      case 'contacted': return 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30';
      case 'converted': return 'bg-green-500/20 text-green-300 border-green-400/30';
      case 'declined': return 'bg-red-500/20 text-red-300 border-red-400/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-400/30';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'new': return 'Neu';
      case 'contacted': return 'Kontaktiert';
      case 'converted': return 'Abgeschlossen';
      case 'declined': return 'Abgelehnt';
      default: return status;
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0F141A] to-[#1A2332] p-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F141A] to-[#1A2332] p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Settings className="w-7 h-7 text-cyan-400" />
              Tuning-Konfigurator
            </h1>
            <p className="text-slate-400 mt-1">
              Einstellungen für Ihren einbettbaren Tuning-Konfigurator
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                try {
                  const response = await axios.post(
                    `${BACKEND_URL}/api/tuning-configurator/toggle-active`,
                    {},
                    { headers }
                  );
                  setSettings(prev => ({ ...prev, is_active: response.data.is_active }));
                  toast.success(response.data.is_active ? 'Konfigurator aktiviert!' : 'Konfigurator deaktiviert');
                } catch (error) {
                  toast.error('Fehler beim Umschalten des Status');
                }
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                settings?.is_active 
                  ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-400/30' 
                  : 'bg-green-500/20 text-green-300 hover:bg-green-500/30 border border-green-400/30'
              }`}
            >
              {settings?.is_active ? 'Deaktivieren' : 'Aktivieren'}
            </button>
            <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              settings?.is_active 
                ? 'bg-green-500/20 text-green-300 border border-green-400/30' 
                : 'bg-red-500/20 text-red-300 border border-red-400/30'
            }`}>
              {settings?.is_active ? 'Aktiv' : 'Inaktiv'}
            </span>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-2">
          {[
            { id: 'settings', label: 'Einstellungen', icon: Settings },
            { id: 'embed', label: 'Einbettung', icon: Code },
            { id: 'inquiries', label: 'Anfragen', icon: Inbox, count: inquiries.filter(i => i.status === 'new').length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                activeTab === tab.id
                  ? 'bg-cyan-500/20 text-cyan-300'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-xs bg-red-500 text-white">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Settings */}
            <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-cyan-400" />
                  Grundeinstellungen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-300">Firmenname</Label>
                  <Input
                    value={settings?.company_name || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Ihre Firma"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Kontakt E-Mail</Label>
                  <Input
                    type="email"
                    value={settings?.contact_email || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                    placeholder="kontakt@firma.ch"
                    className="bg-white/5 border-white/10 text-white mt-1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Anfragen werden an diese E-Mail gesendet
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Pricing Settings */}
            <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-400" />
                  Preiseinstellungen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-slate-300">Preise anzeigen</Label>
                    <p className="text-xs text-slate-500">
                      Zeigt Richtpreise im Konfigurator
                    </p>
                  </div>
                  <Switch
                    checked={settings?.show_prices || false}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_prices: checked }))}
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Preisanpassung (%)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      type="number"
                      value={settings?.price_adjustment_percent || 0}
                      onChange={(e) => setSettings(prev => ({ 
                        ...prev, 
                        price_adjustment_percent: parseFloat(e.target.value) || 0 
                      }))}
                      className="bg-white/5 border-white/10 text-white w-24"
                    />
                    <span className="text-slate-400">%</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Positiv = Aufschlag, Negativ = Rabatt
                  </p>
                </div>
              </CardContent>
            </Card>
            
            {/* Branding */}
            <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10 md:col-span-2">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Palette className="w-5 h-5 text-purple-400" />
                  Design anpassen
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Logo Upload */}
                <div className="mb-6 p-4 border border-white/10 rounded-xl bg-white/5">
                  <Label className="text-slate-300 mb-3 block">Ihr Logo</Label>
                  <div className="flex items-center gap-4">
                    {/* Logo Preview */}
                    <div 
                      className="w-24 h-24 rounded-xl border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden"
                      style={{ backgroundColor: settings?.branding?.background_color || '#151C28' }}
                    >
                      {settings?.branding?.logo_url ? (
                        <img 
                          src={settings.branding.logo_url} 
                          alt="Logo" 
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <span className="text-slate-500 text-xs text-center px-2">Kein Logo</span>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <p className="text-xs text-slate-400 mb-2">
                        Laden Sie Ihr Firmenlogo hoch. Empfohlen: PNG mit transparentem Hintergrund, max 500KB.
                      </p>
                      <div className="flex gap-2">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              if (file.size > 500 * 1024) {
                                toast.error('Logo zu groß. Max 500KB erlaubt.');
                                return;
                              }
                              
                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                
                                const response = await axios.post(
                                  `${BACKEND_URL}/api/upload/logo`,
                                  formData,
                                  { 
                                    headers: { 
                                      ...headers,
                                      'Content-Type': 'multipart/form-data'
                                    }
                                  }
                                );
                                
                                if (response.data.url) {
                                  updateBranding('logo_url', response.data.url);
                                  toast.success('Logo hochgeladen!');
                                }
                              } catch (error) {
                                console.error('Logo upload error:', error);
                                toast.error('Fehler beim Hochladen');
                              }
                            }}
                          />
                          <span className="px-3 py-1.5 bg-cyan-500/20 text-cyan-300 rounded-lg text-sm hover:bg-cyan-500/30 transition-colors inline-block">
                            Logo hochladen
                          </span>
                        </label>
                        {settings?.branding?.logo_url && (
                          <button
                            onClick={() => updateBranding('logo_url', '')}
                            className="px-3 py-1.5 bg-red-500/20 text-red-300 rounded-lg text-sm hover:bg-red-500/30 transition-colors"
                          >
                            Entfernen
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-slate-300">Primärfarbe</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={settings?.branding?.primary_color || '#1EA0E6'}
                        onChange={(e) => updateBranding('primary_color', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={settings?.branding?.primary_color || '#1EA0E6'}
                        onChange={(e) => updateBranding('primary_color', e.target.value)}
                        className="bg-white/5 border-white/10 text-white flex-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Sekundärfarbe</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={settings?.branding?.secondary_color || '#0F141A'}
                        onChange={(e) => updateBranding('secondary_color', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={settings?.branding?.secondary_color || '#0F141A'}
                        onChange={(e) => updateBranding('secondary_color', e.target.value)}
                        className="bg-white/5 border-white/10 text-white flex-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Akzentfarbe</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={settings?.branding?.accent_color || '#7CD4FD'}
                        onChange={(e) => updateBranding('accent_color', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={settings?.branding?.accent_color || '#7CD4FD'}
                        onChange={(e) => updateBranding('accent_color', e.target.value)}
                        className="bg-white/5 border-white/10 text-white flex-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-300">Hintergrund</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={settings?.branding?.background_color || '#151C28'}
                        onChange={(e) => updateBranding('background_color', e.target.value)}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={settings?.branding?.background_color || '#151C28'}
                        onChange={(e) => updateBranding('background_color', e.target.value)}
                        className="bg-white/5 border-white/10 text-white flex-1"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Preview */}
                <div className="mt-6 p-4 rounded-xl border border-white/10" 
                     style={{ backgroundColor: settings?.branding?.background_color || '#151C28' }}>
                  <p className="text-sm text-slate-400 mb-2">Vorschau:</p>
                  <div className="flex items-center gap-4">
                    <div 
                      className="px-4 py-2 rounded-lg font-medium"
                      style={{ 
                        backgroundColor: settings?.branding?.primary_color || '#1EA0E6',
                        color: '#fff'
                      }}
                    >
                      Primär Button
                    </div>
                    <div 
                      className="px-4 py-2 rounded-lg font-medium border-2"
                      style={{ 
                        borderColor: settings?.branding?.accent_color || '#7CD4FD',
                        color: settings?.branding?.accent_color || '#7CD4FD'
                      }}
                    >
                      Akzent Button
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Save Button */}
            <div className="md:col-span-2 flex justify-end">
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-cyan-500 hover:bg-cyan-600 text-white px-8"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Einstellungen speichern
              </Button>
            </div>
          </div>
        )}
        
        {/* Embed Tab */}
        {activeTab === 'embed' && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Code className="w-5 h-5 text-cyan-400" />
                  Einbettungscode
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-400">
                  Fügen Sie diesen Code in Ihre Website ein, um den Tuning-Konfigurator anzuzeigen.
                </p>
                
                <div className="relative">
                  <pre className="p-4 rounded-xl bg-black/50 border border-white/10 text-green-400 text-sm overflow-x-auto">
                    {embedCode?.embed_code || 'Lade...'}
                  </pre>
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(embedCode?.embed_code)}
                    className="absolute top-2 right-2 bg-white/10 hover:bg-white/20"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label className="text-slate-300">Direkt-Link</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        value={embedCode?.embed_url || ''}
                        readOnly
                        className="bg-white/5 border-white/10 text-white"
                      />
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => copyToClipboard(embedCode?.embed_url)}
                        className="border-white/20"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => window.open(embedCode?.embed_url, '_blank')}
                        className="border-white/20"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Globe className="w-5 h-5 text-green-400" />
                  API-Key
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-slate-400">
                  Ihr API-Key für den Konfigurator. Halten Sie diesen geheim.
                </p>
                
                <div className="flex items-center gap-2">
                  <Input
                    type="password"
                    value={settings?.api_key || ''}
                    readOnly
                    className="bg-white/5 border-white/10 text-white font-mono"
                  />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => copyToClipboard(settings?.api_key)}
                    className="border-white/20"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleRegenerateKey}
                    className="border-white/20 text-white"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Neu generieren
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <Card className="bg-gradient-to-br from-[#1A2332] to-[#151C28] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Inbox className="w-5 h-5 text-cyan-400" />
                  Anfragen ({inquiries.length})
                </span>
                <Button onClick={loadData} variant="ghost" size="sm" className="text-slate-400">
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {inquiries.length > 0 ? (
                <div className="space-y-3">
                  {inquiries.map((inquiry) => (
                    <div 
                      key={inquiry.id}
                      className="p-4 rounded-xl bg-white/5 border border-white/10"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-white">
                            {inquiry.vehicle?.brand} {inquiry.vehicle?.model} {inquiry.vehicle?.engine}
                          </h4>
                          <p className="text-cyan-400 text-sm">
                            {inquiry.selected_stage?.toUpperCase()} 
                            {inquiry.selected_options?.length > 0 && ` + ${inquiry.selected_options.length} Optionen`}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs border ${getStatusColor(inquiry.status)}`}>
                          {getStatusLabel(inquiry.status)}
                        </span>
                      </div>
                      
                      <div className="mt-3 pt-3 border-t border-white/10 grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-400">Kunde</p>
                          <p className="text-white">{inquiry.customer?.name || 'N/A'}</p>
                          <p className="text-slate-500">{inquiry.customer?.email}</p>
                          {inquiry.customer?.phone && (
                            <p className="text-slate-500">{inquiry.customer?.phone}</p>
                          )}
                        </div>
                        <div>
                          <p className="text-slate-400">Geschätzter Preis</p>
                          <p className="text-green-400 font-semibold">
                            CHF {inquiry.estimated_price || '—'}
                          </p>
                          <p className="text-slate-500 flex items-center gap-1 mt-1">
                            <Clock className="w-3 h-3" />
                            {new Date(inquiry.created_at).toLocaleDateString('de-CH')}
                          </p>
                        </div>
                      </div>
                      
                      {inquiry.customer?.message && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <p className="text-slate-400 text-sm">Nachricht:</p>
                          <p className="text-slate-300 text-sm">{inquiry.customer.message}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <Inbox className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>Noch keine Anfragen eingegangen</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TuningConfiguratorSettings;
